
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { faUserCircle } from "@fortawesome/free-regular-svg-icons";
import { Row, Col, Nav, Form, Image, Navbar, Dropdown, Container, ListGroup, Modal, Button } from '@themesberg/react-bootstrap';

import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import { Link } from "react-router-dom";
import { Routes } from "../routes";

const NavbarTop = (props) => {
  const { currentUser, logout, refreshCurrentUser } = useAuth();
  const [showChangePass, setShowChangePass] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleChangePassword = async () => {
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const res = await api.post("/change-password", { oldPassword, newPassword });
      if (res.data && res.data.error) {
        setErrorMsg(res.data.message || "Failed to change password");
      } else {
        setSuccessMsg("Password changed successfully");
        setOldPassword("");
        setNewPassword("");
        setTimeout(() => setShowChangePass(false), 800);
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e.message || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    // Refresh currentUser data (profile image/name) but avoid loops
    let mounted = true;
    const userId = currentUser?.id;
    if (!userId) return () => { mounted = false };

    (async () => {
      try {
        // Prefer context refresh if provided
        if (typeof refreshCurrentUser === 'function') {
          const updated = await refreshCurrentUser();
          if (mounted && updated) return;
        }

        // Fallback: call API directly and update localStorage only when data actually changed
        const { data } = await api.get(`/users/${userId}`);
        const user = data.data;
        if (user && (user.uri || user.name)) {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          const uriChanged = user.uri && user.uri !== stored.uri;
          const nameChanged = user.name && user.name !== stored.name;
          // If nothing changed, do not write to localStorage or dispatch event (prevents loops)
          if (!uriChanged && !nameChanged) return;

          const merged = { ...stored, ...user };
          localStorage.setItem('user', JSON.stringify(merged));
          // Inform other parts of the app that user was updated by dispatching a custom event
          window.dispatchEvent(new CustomEvent('user:updated', { detail: merged }));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false };
  }, [currentUser?.id]);

  return (
    <>
      <Navbar variant="dark" expanded className="ps-0 pe-2 pb-0">
        <Container fluid className="px-0">
          <div className="d-flex justify-content-between w-100">
            <div className="d-flex align-items-center" />
            <Nav className="align-items-center">
              <Dropdown as={Nav.Item}>
                <Dropdown.Toggle as={Nav.Link} className="pt-1 px-0">
                  <div className="media d-flex align-items-center">
                    <Image src={currentUser?.uri} className="user-avatar md-avatar rounded-circle" />
                    <div className="media-body ms-2 text-dark align-items-center d-none d-lg-block">
                      <span className="mb-0 font-small fw-bold">{currentUser?.name}</span>
                    </div>
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu className="user-dropdown dropdown-menu-right mt-2">
                  <Dropdown.Item className="fw-bold" as={Link} to={Routes.MyProfileEdit.path.replace(':id', currentUser?.id || '')}>
                    <FontAwesomeIcon icon={faUserCircle} className="me-2" /> My Profile
                  </Dropdown.Item>
                  <Dropdown.Item className="fw-bold" onClick={() => setShowChangePass(true)}>
                    <FontAwesomeIcon icon={faCog} className="me-2" /> Change Password
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item className="fw-bold" onClick={logout}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="text-danger me-2"/> Logout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </div>
        </Container>
      </Navbar>
      <Modal show={showChangePass} onHide={() => setShowChangePass(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMsg ? <div className="alert alert-danger" role="alert">{errorMsg}</div> : null}
          {successMsg ? <div className="alert alert-success" role="alert">{successMsg}</div> : null}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Old Password</Form.Label>
              <Form.Control type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} placeholder="Enter old password" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowChangePass(false)} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleChangePassword} disabled={saving || !oldPassword || !newPassword}>
            {saving ? 'Saving...' : 'Change Password'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NavbarTop;
